import { defineStore } from 'pinia'
import APIRequest from '@/api'
import { AppSupportLanguage } from '@/api/Models/AppSupportLanguage'
import { AppLanguagesDict } from '@/api/Models/AppLanguagesDict'
import i18n from '@/locales'
import { ref, type WritableComputedRef } from 'vue'
import { isApp, getLang } from '@/utils/client'
import { isTudoApp } from '@/utils/tudo'
import { isOutFieldApp } from '@/utils/outfield'
import { isFeishu } from '@/utils/feishu'
import { getBranding, getLanguageVersion } from '@/api/requests/AppRequests'
import { isWisdomRabbitApp } from '@/utils/sandsTalkSmartRabbit'
// import * as queryString from 'querystring'
export type LocaleType = 'zh-cn' | 'en-us' | 'ru' | 'ja' | 'ko'

const getLangKey = (browerKey: string) => {
  switch (browerKey) {
    case 'zh-CN':
      return 'zh-cn'
    case 'zh-TW':
      return 'zh-cht'
    case 'zh-HK':
      return 'zh-cht'
    case 'en-US':
      return 'en-us'
    default:
      return browerKey.slice(0, 2).toLowerCase()
  }
}
const validateLang = (lang: string, langs: AppSupportLanguage[]) => {
  // lang = getLangKey(lang)
  return langs.find((item) => {
    return lang === item.languageCode
  })
}

interface TState {
  lang: string
  langs: AppSupportLanguage[]
  langsJson: AppLanguagesDict[]
  cachePageList: string[]
}

export const useSystemStore = defineStore('system', {
  state: (): TState => {
    return {
      lang: localStorage.getItem('lang') || navigator.language?.toLowerCase() || 'zh-cn',
      langs: [],
      langsJson: [],
      cachePageList: ['tab', 'shopIndex', 'communitySearchPage', 'task', 'newsDetail']
    }
  },
  actions: {
    setLang(lang: LocaleType): void {
      this.lang = lang
      localStorage.setItem('lang', lang)
    },

    async initializeLanguage() {
      const cachedLangsJson = localStorage.getItem('standsTalkLanguage')
      if (cachedLangsJson) {
        this.langsJson = JSON.parse(cachedLangsJson)
        i18n.global.setLocaleMessage(this.lang, this.langsJson)
        i18n.global.locale.value = this.lang as LocaleType
      } else {
        await this.getAppSupportLanguage()
      }
    },

    async getAppSupportLanguage(callback?: () => void) {
      i18n.global.locale.value = this.lang
      if (this.lang === 'zh-cn') {
        i18n.global.setLocaleMessage(this.lang, require('@/locales/languages/zh.json'))
      } else {
        i18n.global.setLocaleMessage(this.lang, require('@/locales/languages/en.json'))
      }

      const getTenantId = localStorage.getItem('sandsTalkTenantId')
      const res = await APIRequest.App.getAppSupportLanguage({ tenantId: getTenantId || '' })
      if (res.code === 1) {
        this.langs = res.data
        const storageLang = localStorage.getItem('lang')
        const storageLangVersion = localStorage.getItem('languageVersion')
        const browerLang = navigator.language
        let langCache = 'zh-cn'

        if (isApp) {
          langCache = getLang()
        } else if (isTudoApp) {
          langCache = 'zh-cn'
        } else if (isWisdomRabbitApp) {
          langCache = 'zh-cn'
        } else if (isOutFieldApp) {
          // 外场默认中文
          langCache = 'zh-cn'
        } else if (storageLang && !isFeishu) {
          langCache = storageLang
        } else if (browerLang) {
          langCache = getLangKey(browerLang)
        }

        if (validateLang(langCache, res.data)) {
          this.lang = langCache
        } else {
          this.lang = 'zh-cn'
        }

        try {
          if (isApp) {
            const cachedLang = localStorage.getItem('lang')
            if (getLang() && cachedLang !== getLang()) {
              localStorage.setItem('lang', this.lang)
              await this.getAppLanguagesDict(getLang())
            }
          } else {
            localStorage.setItem('lang', this.lang)
            const { data } = await getLanguageVersion()
            localStorage.setItem('languageVersion', data.toString())
            if (storageLangVersion === null) {
              await this.getAppLanguagesDict(this.lang)
            } else if (navigator.language?.toLowerCase() !== storageLangVersion) {
              await this.getAppLanguagesDict(navigator.language?.toLowerCase())
            } else if (storageLangVersion && data.toString() > storageLangVersion) {
              await this.getAppLanguagesDict(this.lang)
            } else {
              this.langsJson = JSON.parse(localStorage.getItem('standsTalkLanguage') || '[]')
              if (this.langsJson.length > 0) {
                i18n.global.setLocaleMessage(this.lang, this.langsJson)
                i18n.global.locale.value = this.lang as LocaleType
              }
            }
          }
        } catch (error) {
          console.log(error)
        }

        callback && callback()
      }
    },
    async getAppLanguagesDict(val: string) {
      const res = await APIRequest.App.getAppLanguagesDict()
      if (res.code === 1) {
        i18n.global.setLocaleMessage(val, res.data)
        this.lang = val
        if (i18n.mode === 'legacy') {
          i18n.global.locale = val as unknown as WritableComputedRef<LocaleType>
        } else {
          i18n.global.locale.value = val as LocaleType
        }
        this.langsJson = res.data
        localStorage.setItem('standsTalkLanguage', JSON.stringify(res.data))
      }
    },
    setCachePageList(page: string[]) {
      this.cachePageList = page
    },
    resetCachePageList() {
      this.cachePageList = ['tab', 'shopIndex', 'communitySearchPage', 'task', 'newsDetail', 'shopIndexTab']
    },
    deleteCachePageList(page: string) {
      this.cachePageList = this.cachePageList.filter((item) => item !== page)
    }
  }
})

// 在 useSystemStore 中添加主题相关状态
const themeColor = ref('#f39b00')
const backgroundImageUrl = ref('')
const siteTitle = ref('') // 新增站点标题
const icoUrl = ref('') // 新增图标URL
const isThemeLoaded = ref(false)

// 添加获取主题的方法
const loadTheme = async () => {
  try {
    const tenantId = localStorage.getItem('sandsTalkTenantId')
    if (tenantId) {
      // 检查是否已经有缓存的主题数据，如果没有或者tenantId发生变化，则重新加载
      const cachedTenantId = localStorage.getItem('cachedTenantId')
      const hasValidCache = cachedTenantId === tenantId &&
        localStorage.getItem('sandsTalkThemeColor') &&
        localStorage.getItem('sandsTalkBackgroundUrl')

      if (isThemeLoaded.value && hasValidCache) {
        return // 已加载过且缓存有效，直接返回
      }

      const currentHostname = window.location.hostname
      const isPROOrUAT = currentHostname === 'sandstalk.ymdd.tech' || currentHostname === 'sandstalk-sit.ymdd.tech'

      // 如果是企微免登且在PRO/UAT环境，identifier使用1000，否则使用tenantId
      const identifier = (isPROOrUAT) ? '1000' : tenantId

      const backgroundRes = await getBranding({
        clientType: 2,
        identifier,
        type: 1
      })

      if (backgroundRes.code === 1) {
        backgroundImageUrl.value = backgroundRes?.data?.backgroundUrl || require('@/assets/images/user/loginBg.png')
        themeColor.value = backgroundRes?.data?.themeColorCode || '#f39b00'

        // 新增：处理站点标题和图标
        if (backgroundRes?.data?.siteTitle) {
          siteTitle.value = backgroundRes.data.siteTitle
          document.title = siteTitle.value
          // 缓存站点标题
          localStorage.setItem('sandsTalkSiteTitle', siteTitle.value)
        }

        if (backgroundRes?.data?.icoUrl) {
          icoUrl.value = backgroundRes.data.icoUrl
          updateFavicon(icoUrl.value)
          // 缓存图标URL
          localStorage.setItem('sandsTalkIcoUrl', icoUrl.value)
        }

        // 缓存到 localStorage
        localStorage.setItem('sandsTalkThemeColor', themeColor.value)
        localStorage.setItem('sandsTalkBackgroundUrl', backgroundImageUrl.value)
        // 缓存当前的tenantId，用于判断是否需要重新加载
        localStorage.setItem('cachedTenantId', tenantId)

        isThemeLoaded.value = true
      }
    }
  } catch (error) {
    console.error('Failed to load theme:', error)
    // 发生错误时不设置isThemeLoaded为true，允许重试
  }
}

// 新增：更新网站图标的函数
const updateFavicon = (iconUrl: string) => {
  try {
    // 移除现有的 favicon
    const existingFavicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement
    if (existingFavicon) {
      existingFavicon.remove()
    }

    // 创建新的 favicon
    const newFavicon = document.createElement('link')
    newFavicon.rel = 'icon'
    newFavicon.type = 'image/x-icon'
    newFavicon.href = iconUrl

    // 添加到 head
    document.head.appendChild(newFavicon)
  } catch (error) {
    console.error('Failed to update favicon:', error)
  }
}

// 初始化时从缓存读取
const initThemeFromCache = () => {
  const cachedThemeColor = localStorage.getItem('sandsTalkThemeColor')
  const cachedBackgroundUrl = localStorage.getItem('sandsTalkBackgroundUrl')
  const cachedSiteTitle = localStorage.getItem('sandsTalkSiteTitle')
  const cachedIcoUrl = localStorage.getItem('sandsTalkIcoUrl')

  if (cachedThemeColor) {
    themeColor.value = cachedThemeColor
  }
  if (cachedBackgroundUrl) {
    backgroundImageUrl.value = cachedBackgroundUrl
  }
  if (cachedSiteTitle) {
    siteTitle.value = cachedSiteTitle
    document.title = cachedSiteTitle
  }
  if (cachedIcoUrl) {
    icoUrl.value = cachedIcoUrl
    updateFavicon(cachedIcoUrl)
  }
}

// 导出主题相关的功能
export {
  themeColor,
  backgroundImageUrl,
  siteTitle, // 新增导出
  icoUrl, // 新增导出
  isThemeLoaded,
  loadTheme,
  initThemeFromCache,
  updateFavicon // 新增导出
}
